import classNames from 'classnames';
import { m } from 'framer-motion';
import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import AlertBanner from '@/components/molecules/alert-banner/alert-banner';
import Checkbox from '@/components/molecules/checkbox/checkbox';
import Select, { SelectOption } from '@/components/molecules/select/select';
import Tooltip from '@/components/molecules/tooltip/tooltip';
import QuantitySelector from '@/components/organisms/quantity-selector/quantity-selector';
import {
  Room,
  RoomsState,
} from '@/components/organisms/search-bar/search-bar.types';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import SearchBarSectionMobileWrapper from '../search-bar-section-mobile-wrapper/search-bar-section-mobile-wrapper';
import styles from './search-bar-persons-dropdown.module.scss';

const MAXIMUM_CHILDREN_PER_ROOM = 12;
const MINIMUM_CHILDREN_PER_ROOM = 0;
const MINIMUM_ADULTS_PER_ROOM = 1;

export interface SearchBarPersonsDropdownProps {
  /** Age threshold that separates adults from children */
  adultThreshold?: number;
  /** The class name to apply */
  className?: string;
  /** Default number of adults per room */
  defaultAdultNumber?: number;
  /** The description to show in the mobile wrapper */
  description?: string | string[];
  /** Indicates whether to hide the room distribution */
  hideRoomDistribution?: boolean;
  /** Indicates whether the dropdown is notified */
  isNotified?: boolean;
  /** Indicates whether the dropdown is visible */
  isVisible?: boolean;
  /** Indicates whether the dropdown is used in the search widget */
  isWidget?: boolean;
  /** The label to show in the mobile wrapper */
  label: string;
  /** Maximum number of persons */
  maxPersons?: number;
  /** Maximum number of rooms */
  maxRooms?: number;
  /** An event that is triggered on change */
  onChange?: (roomsState: RoomsState) => void;
  /** An event that is triggered on click back */
  onClickBack?: () => void;
  /** An event that is triggered on click close */
  onClickClose?: () => void;
  /** An event that is triggered on click next */
  onClickNext?: () => void;
  /** Warning message to display when unchecking automatic room distribution */
  roomDistributionWarning?: string;
  /** The room list */
  roomsState?: RoomsState;
}

const SearchBarPersonsDropdown = memo(
  forwardRef(
    (
      {
        adultThreshold = 17,
        className,
        defaultAdultNumber = 0,
        description,
        isNotified = false,
        isVisible = false,
        isWidget = false,
        label,
        maxPersons = 5,
        maxRooms = 3,
        onChange,
        onClickBack,
        onClickClose,
        onClickNext,
        roomDistributionWarning,
        roomsState = {
          automaticRoomAssignment: true,
          rooms: [
            {
              childrenAges: [],
              numberOfAdults: defaultAdultNumber,
              numberOfChildren: 0,
            },
          ],
        },
      }: SearchBarPersonsDropdownProps,
      ref,
    ) => {
      const isTablet = useWindowSize('tablet');
      const isLaptop = useWindowSize('laptop');
      const { t } = useI18n();
      const initialRoomsStateRef = useRef<RoomsState | null>(null);
      const [showValidationErrors, setShowValidationErrors] = useState(false);

      const ageOptions: SelectOption[] = useMemo(
        () =>
          Array.from({ length: adultThreshold + 1 }, (_, index) => ({
            name: index.toString(),
            value: index,
          })),
        [adultThreshold],
      );

      const handleIncrementChildren = useCallback(
        (roomIndex: number) => {
          const newRooms = [...roomsState.rooms];
          const newNumberOfChildren = newRooms[roomIndex].numberOfChildren + 1;

          newRooms[roomIndex] = {
            ...newRooms[roomIndex],
            childrenAges: [...newRooms[roomIndex].childrenAges, null],
            numberOfChildren: newNumberOfChildren,
          };

          initialRoomsStateRef.current = null;

          onChange?.({ ...roomsState, rooms: newRooms });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [roomsState],
      );

      const handleDecrementChildren = useCallback(
        (roomIndex: number) => {
          const newRooms = [...roomsState.rooms];
          const newNumberOfChildren = newRooms[roomIndex].numberOfChildren - 1;

          newRooms[roomIndex] = {
            ...newRooms[roomIndex],
            childrenAges: newRooms[roomIndex].childrenAges.slice(
              0,
              newNumberOfChildren,
            ),
            numberOfChildren: newNumberOfChildren,
          };

          initialRoomsStateRef.current = null;

          onChange?.({ ...roomsState, rooms: newRooms });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [roomsState],
      );

      const handleIncrementAdults = useCallback(
        (roomIndex: number) => {
          const newRooms = [...roomsState.rooms];

          newRooms[roomIndex] = {
            ...newRooms[roomIndex],
            numberOfAdults: newRooms[roomIndex].numberOfAdults + 1,
          };

          initialRoomsStateRef.current = null;

          onChange?.({ ...roomsState, rooms: newRooms });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [roomsState],
      );

      const handleDecrementAdults = useCallback(
        (roomIndex: number) => {
          const newRooms = [...roomsState.rooms];

          newRooms[roomIndex] = {
            ...newRooms[roomIndex],
            numberOfAdults: newRooms[roomIndex].numberOfAdults - 1,
          };

          initialRoomsStateRef.current = null;

          onChange?.({ ...roomsState, rooms: newRooms });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [roomsState],
      );

      const handleChangeChildAge = useCallback(
        (
          roomIndex: number,
          childIndex: number,
          event: React.ChangeEvent<HTMLSelectElement>,
        ) => {
          const newAge = parseInt(event.target.value, 10);

          const newRooms = [...roomsState.rooms];
          const newChildrenAges = [...newRooms[roomIndex].childrenAges];

          newChildrenAges[childIndex] = isNaN(newAge) ? null : newAge;
          newRooms[roomIndex] = {
            ...newRooms[roomIndex],
            childrenAges: newChildrenAges,
          };

          initialRoomsStateRef.current = null;

          onChange?.({ ...roomsState, rooms: newRooms });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [roomsState],
      );

      const handleClickClear = useCallback(() => {
        initialRoomsStateRef.current = null;

        onChange?.({
          automaticRoomAssignment: true,
          rooms: [
            {
              childrenAges: [],
              numberOfAdults: defaultAdultNumber,
              numberOfChildren: 0,
            },
          ],
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [defaultAdultNumber]);

      const handleAddRoom = useCallback(() => {
        if (roomsState.rooms.length < maxRooms) {
          const newRoomAssignment = false;

          const newRooms = [
            ...roomsState.rooms,
            {
              childrenAges: [],
              numberOfAdults: defaultAdultNumber,
              numberOfChildren: 0,
            },
          ];

          initialRoomsStateRef.current = null;

          onChange?.({
            automaticRoomAssignment: newRoomAssignment,
            rooms: newRooms,
          });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [defaultAdultNumber, maxRooms, roomsState]);

      const handleDeleteRoom = useCallback(
        (roomIndex: number) => {
          if (roomsState.rooms.length > 1) {
            const newRooms = roomsState.rooms.filter(
              (_, index) => index !== roomIndex,
            );

            initialRoomsStateRef.current = null;

            onChange?.({ ...roomsState, rooms: newRooms });
          }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [roomsState],
      );

      const handleAutomaticRoomAssignment = useCallback(() => {
        if (initialRoomsStateRef.current === null) {
          initialRoomsStateRef.current = roomsState;
        }

        const newRoomAssignment = !roomsState?.automaticRoomAssignment;

        const newRooms = newRoomAssignment
          ? [
              {
                childrenAges: roomsState?.rooms?.flatMap(
                  room => room.childrenAges,
                ),
                numberOfAdults: roomsState?.rooms?.reduce(
                  (total, room) => total + room.numberOfAdults,
                  0,
                ),
                numberOfChildren: roomsState?.rooms?.reduce(
                  (total, room) => total + room.numberOfChildren,
                  0,
                ),
              },
            ]
          : initialRoomsStateRef.current?.rooms || roomsState?.rooms;

        onChange?.({
          automaticRoomAssignment: newRoomAssignment,
          rooms: newRooms,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [roomsState]);

      const totalAdultCount = useMemo(() => {
        return roomsState.rooms.reduce((total, room) => {
          return total + room.numberOfAdults;
        }, 0);
      }, [roomsState.rooms]);

      const shouldShowAddRoomButton =
        roomsState.rooms.length < maxRooms &&
        totalAdultCount + defaultAdultNumber <= maxPersons;

      const isSectionValid = useCallback((rooms: Room[]): boolean => {
        return rooms.every(room => {
          if (room.numberOfChildren > 0) {
            return (
              room.childrenAges.length === room.numberOfChildren &&
              room.childrenAges.every(age => age !== null)
            );
          }

          return true;
        });
      }, []);

      const handleClickNext = useCallback(() => {
        if (isSectionValid(roomsState.rooms)) {
          setShowValidationErrors(false);
          onClickNext?.();
        } else {
          setShowValidationErrors(true);
        }
      }, [isSectionValid, onClickNext, roomsState.rooms]);

      const warningMessage = useMemo(() => {
        if (roomsState.automaticRoomAssignment || !roomDistributionWarning) {
          return null;
        }

        return (
          <AlertBanner
            className={styles.warningContainer}
            text={roomDistributionWarning}
          />
        );
      }, [roomDistributionWarning, roomsState.automaticRoomAssignment]);

      const renderRooms = () => (
        <React.Fragment>
          <div className={styles.roomList}>
            {roomsState.rooms.map((room, roomIndex) => (
              <div key={roomIndex}>
                <div className={styles.roomHeading}>
                  <Typography tag="label" tagStyle="labelMedium">
                    {roomsState.rooms.length === 1
                      ? t('items.search.searchBar.persons.persons')
                      : `${t('items.search.searchBar.persons.persons')} ${t('items.search.searchBar.persons.inRoom')} ${roomIndex + 1}`}
                  </Typography>

                  {roomIndex > 0 && (
                    <Link
                      className={styles.deleteButton}
                      onClick={() => handleDeleteRoom(roomIndex)}
                    >
                      <Typography tagStyle="bodySmall">
                        {t('items.search.searchBar.persons.deleteRoom')}
                      </Typography>

                      <Icon aria-hidden="true" name="Delete" size={16} />
                    </Link>
                  )}
                </div>

                <QuantitySelector
                  className={classNames(
                    styles.quantitySelector,
                    styles.isAdult,
                  )}
                  description={t(
                    'items.search.searchBar.persons.overYearsOld',
                    {
                      adultThreshold,
                    },
                  )}
                  label={t('items.search.searchBar.persons.adults')}
                  maximumQuantity={
                    maxPersons - totalAdultCount + room.numberOfAdults
                  }
                  minimumQuantity={MINIMUM_ADULTS_PER_ROOM}
                  onClickDecrement={() => handleDecrementAdults(roomIndex)}
                  onClickIncrement={() => handleIncrementAdults(roomIndex)}
                  value={room.numberOfAdults}
                />

                <QuantitySelector
                  className={styles.quantitySelector}
                  description={t(
                    'items.search.searchBar.persons.betweenYearsOld',
                    { adultThreshold },
                  )}
                  label={t('items.search.searchBar.persons.children')}
                  maximumQuantity={MAXIMUM_CHILDREN_PER_ROOM}
                  minimumQuantity={MINIMUM_CHILDREN_PER_ROOM}
                  onClickDecrement={() => handleDecrementChildren(roomIndex)}
                  onClickIncrement={() => handleIncrementChildren(roomIndex)}
                  value={room.numberOfChildren}
                />

                <div className={styles.ageSelectorWrapper}>
                  {Array.from(
                    { length: room.numberOfChildren },
                    (_, childIndex) => (
                      <div key={childIndex} className={styles.ageSelectorRow}>
                        <Typography className={styles.ageSelectorLabel}>
                          {t('items.search.searchBar.persons.ageOfChild')}
                          {childIndex + 1}:
                        </Typography>

                        <Select
                          className={styles.select}
                          error={
                            (showValidationErrors || isNotified) &&
                            !room.childrenAges[childIndex]?.toString()
                              ? t(
                                  'items.search.searchBar.persons.ageSelectorError',
                                )
                              : ''
                          }
                          label={t('items.search.searchBar.persons.age')}
                          onChange={event =>
                            handleChangeChildAge(roomIndex, childIndex, event)
                          }
                          options={ageOptions}
                          placeholder={t(
                            'items.search.searchBar.persons.ageSelectorPlaceholder',
                          )}
                          required
                          value={
                            room.childrenAges[childIndex]?.toString() || ''
                          }
                        />
                      </div>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.addRoomWrapper}>
            {shouldShowAddRoomButton && (
              <Link className={styles.link} onClick={handleAddRoom}>
                <Typography tagStyle="bodyMedium">
                  {t('items.search.searchBar.persons.addExtraRoom')}
                </Typography>
              </Link>
            )}
          </div>

          <div className={styles.checkboxWrapper}>
            <Checkbox
              checked={roomsState.automaticRoomAssignment}
              label={t(
                'items.search.searchBar.persons.automaticRoomAssignment',
              )}
              onChange={() => handleAutomaticRoomAssignment()}
            />
            <Tooltip
              className={styles.info}
              text={t(
                'items.search.searchBar.persons.automaticRoomAssignmentTip',
              )}
            />
          </div>
        </React.Fragment>
      );

      useImperativeHandle(
        ref,
        () => ({
          addRoom: handleAddRoom,
          clear: handleClickClear,
        }),
        [handleAddRoom, handleClickClear],
      );

      if (isLaptop && !isVisible) {
        return null;
      }

      return (
        <div
          className={classNames(styles.component, className, {
            [styles.isWidget]: isWidget,
          })}
        >
          {isTablet ? (
            <m.div
              animate={isVisible ? 'show' : 'hidden'}
              className={classNames(styles.desktopWrapper, {
                [styles.isVisible]: isVisible,
              })}
              exit="hidden"
              initial="hidden"
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              variants={{
                hidden: {
                  opacity: 0,
                  transitionEnd: {
                    display: 'none',
                  },
                  translateY: 0,
                },
                show: {
                  display: 'block',
                  opacity: 1,
                  translateY: 0,
                },
              }}
            >
              <div className={styles.content}>
                {isWidget && (
                  <div className={styles.label}>
                    <Typography tag="label" tagStyle="labelSmall">
                      {t('items.search.searchBar.persons.choosePersons')}
                    </Typography>
                  </div>
                )}

                <div className={styles.dropdownWrapper}>
                  {renderRooms()}

                  {!isWidget && (
                    <div className={styles.linkButtonWrapper}>
                      <Link onClick={handleClickClear}>
                        <Typography
                          className={styles.link}
                          tagStyle="bodyMedium"
                        >
                          {t('items.common.words.clear')}
                        </Typography>
                      </Link>
                    </div>
                  )}
                </div>

                {warningMessage}
              </div>
            </m.div>
          ) : (
            <SearchBarSectionMobileWrapper
              buttonLabel={t('items.common.words.next')}
              description={description}
              isOpen={isVisible}
              label={label}
              onClickBack={onClickBack}
              onClickClear={handleClickClear}
              onClickClose={onClickClose}
              onClickNext={handleClickNext}
              showBackButton
            >
              <div className={styles.content}>
                {renderRooms()}

                {warningMessage}
              </div>
            </SearchBarSectionMobileWrapper>
          )}
        </div>
      );
    },
  ),
);

SearchBarPersonsDropdown.displayName = 'SearchBarPersonsDropdown';

export default SearchBarPersonsDropdown;
